import { api } from '../../lib/api';

export interface CommentAuthor {
  id: number;
  username: string;
  display_name: string | null;
  avatar: string | null;
}

export interface Comment {
  id: number;
  parent_id: number | null;
  /** NULL for a comment about the track; a millisecond offset for a pin. */
  position_ms: number | null;
  created_at: string;
  deleted: boolean;
  /** Null on a deleted comment — the server withholds it, it is not hidden here. */
  body: string | null;
  author: CommentAuthor | null;
  can_delete: boolean;
}

/**
 * A comment that is definitely pinned.
 *
 * The server only ever puts positioned comments in `pins`, so the narrowing is
 * true - and stating it here means the waveform does not have to be handed a
 * type whose `position_ms` might be null and then assert otherwise.
 */
export type PinnedComment = Comment & { position_ms: number };

export interface CommentThread {
  items: Comment[];
  /**
   * Pinned comments in time order.
   *
   * Sent separately from `items` rather than derived: the waveform wants every
   * pin sorted by position and redraws on the playhead, while the thread wants
   * every comment newest-first. Deriving one from the other means re-sorting on
   * every frame.
   */
  pins: PinnedComment[];
  can_comment: boolean;
}

export const comments = {
  forTrack: (trackId: number) => api.get<CommentThread>(`/tracks/${trackId}/comments`),

  create: (trackId: number, input: { body: string; position_ms?: number | null; parent_id?: number | null }) =>
    api.post<Comment>(`/tracks/${trackId}/comments`, input),

  remove: (commentId: number) => api.delete<{ deleted: boolean }>(`/comments/${commentId}`),
};

/** `1:23` — the timestamp a pin is anchored to. */
export function stamp(ms: number): string {
  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
